import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { api } from '../../api/client';
import { REWARD_ICONS, useSettings, type RewardIcon } from '../../settings/SettingsContext';
import { speak, stopSpeaking } from '../speak';
import { EMOJI, spokenText } from '../phrases';
export { spokenText } from '../phrases';
import { playCorrectPing } from '../ping';
import { endSession, fetchRewardSummary, type RewardSummary } from '../../rewards/rewards';
import { registerTrial, respondTrial } from '../trials';
import type { ProgramView } from '../../programs/types';
import { useEntitlements } from '../../subscription/offers';
import { openTalkBoard, useGameBarTalk, useTalkBoardOpen } from '../../talk/talkOpen';

export type Verdict = { correct: boolean; rewarded: number };

export type TrialPort = {
  register(presented: Record<string, unknown>, answer: Record<string, unknown> | null): Promise<string>;
  respond(trialId: string, response: string): Promise<Verdict>;
  rest(trialId: string): Promise<void>;
  live: boolean;
};

export function serverPort(programId: string, learnerId: string | undefined): TrialPort {
  return {
    live: true,
    async register(presented, answer) {
      return (await registerTrial(programId, learnerId, presented, answer)).trialId;
    },
    async respond(trialId, response) {
      const v = await respondTrial(trialId, response);
      return { correct: v.correct, rewarded: v.rewarded };
    },
    async rest(trialId) {
      await api(`/games/rounds/${trialId}/break`, { method: 'POST', token: true }).catch(() => undefined);
    },
  };
}

export function sandboxPort(): TrialPort {
  const answers = new Map<string, string | null>();
  return {
    live: false,
    async register(_presented, answer) {
      const id = crypto.randomUUID();
      const v = answer && typeof answer.value === 'string' ? answer.value : null;
      answers.set(id, v);
      return id;
    },
    async respond(trialId, response) {
      const expected = answers.get(trialId);
      answers.delete(trialId);
      return { correct: expected == null || expected.toLowerCase() === response.trim().toLowerCase(), rewarded: 0 };
    },
    async rest() {},
  };
}

export function shuffle<T>(arr: readonly T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export function pick<T>(arr: readonly T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

export function splitFace(text: string): { picture: string; word: string } {
  const pics = text.match(EMOJI) ?? [];
  return { picture: pics.join(''), word: spokenText(text) };
}

export type GameProps = {
  programId?: string;
  learnerId?: string;
  config?: Record<string, unknown>;
};

export function useGame({ programId, learnerId, config: given }: GameProps) {
  const { settings } = useSettings();
  const [config, setConfig] = useState<Record<string, unknown> | null>(given ?? null);
  const [error, setError] = useState<string | null>(null);
  const [onBreak, setOnBreak] = useState(false);
  const [collected, setCollected] = useState<RewardSummary | null>(null);
  const [earnedNow, setEarnedNow] = useState(0);
  const portRef = useRef<TrialPort>(given || !programId ? sandboxPort() : serverPort(programId, learnerId));
  const answeredRef = useRef(false);
  const sessionRef = useRef(crypto.randomUUID());
  const live = portRef.current.live;

  useEffect(() => {
    if (given || !programId) return;
    let cancelled = false;
    void api<ProgramView>(`/programs/${programId}`, { token: true })
      .then((p) => { if (!cancelled) setConfig(p.config ?? {}); })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : 'Could not load the program'); });
    return () => { cancelled = true; };
  }, [programId, given]);

  useEffect(() => {
    if (!live || !settings.features.rewardTotal) return;
    let cancelled = false;
    void fetchRewardSummary(learnerId ?? null).then((s) => { if (!cancelled) setCollected(s); });
    return () => { cancelled = true; };
  }, [learnerId, settings.features.rewardTotal, live]);

  useEffect(() => {
    const session = sessionRef.current;
    return () => {
      stopSpeaking();
      if (live && programId) void endSession(programId, session, answeredRef.current);
    };
  }, [programId, live]);

  const say = useCallback((text: string) => {
    const words = spokenText(text);
    if (!words) return;
    void speak(words, { voiceName: settings.ttsVoice, rate: settings.ttsRate });
  }, [settings.ttsVoice, settings.ttsRate]);

  const register = useCallback(async (presented: Record<string, unknown>, answer: Record<string, unknown> | null) => {
    try {
      return await portRef.current.register(presented, answer);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start the round');
      return null;
    }
  }, []);

  const respond = useCallback(async (trialId: string, response: string): Promise<Verdict | null> => {
    try {
      const v = await portRef.current.respond(trialId, response);
      answeredRef.current = true;
      if (v.rewarded > 0) setEarnedNow((n) => n + v.rewarded);
      if (v.correct && settings.correctPingEnabled) playCorrectPing();
      return v;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not record that answer');
      return null;
    }
  }, [settings.correctPingEnabled]);

  const takeBreak = useCallback(async (trialId: string | null) => {
    stopSpeaking();
    if (trialId) await portRef.current.rest(trialId);
    setOnBreak(true);
  }, []);

  const str = (key: string, fallback: string) => {
    const v = config?.[key];
    return typeof v === 'string' ? v : fallback;
  };
  const num = (key: string, fallback: number) => {
    const v = config?.[key];
    return typeof v === 'number' ? v : fallback;
  };
  const bool = (key: string, fallback: boolean) => {
    const v = config?.[key];
    return typeof v === 'boolean' ? v : fallback;
  };
  const errorless = str('errorless', 'off') === 'on';
  const textPrompt = str('promptMode', 'AUDIO') === 'TEXT';
  const transitionMs = settings.roundTransitionType === 'NONE' ? 0 : settings.roundTransitionMs;

  return {
    settings, config, error, setError, onBreak, setOnBreak, collected, earnedNow,
    say, register, respond, takeBreak, str, num, bool, errorless, textPrompt, transitionMs, live,
  };
}

export type Game = ReturnType<typeof useGame>;

export function GameFrame({ game, empty, emptyText, onResume, breakTrial, children }: {
  game: Game;
  empty: boolean;
  emptyText: string;
  onResume: () => void;
  breakTrial: string | null;
  children: ReactNode;
}) {
  const { settings, collected, earnedNow } = game;
  const talk = !!useEntitlements()?.talkBoard;
  useGameBarTalk(talk && !game.error && !!game.config && !empty && !game.onBreak);
  if (game.error) return <div className="card"><div className="error" role="alert">{game.error}</div></div>;
  if (!game.config) return <p className="muted">Loading…</p>;
  if (empty) {
    return (
      <div className="card">
        <p>This program has nothing to practise yet.</p>
        <p className="field-hint">{emptyText}</p>
      </div>
    );
  }
  if (game.onBreak) {
    return (
      <div className="card">
        <h2>Break time</h2>
        <p className="field-hint">Asking to stop is written down as asking to stop. It is not a wrong answer.</p>
        <div className="toolbar">
          <button className="btn" onClick={() => { game.setOnBreak(false); onResume(); }}>Ready to carry on</button>
        </div>
      </div>
    );
  }
  return (
    <>
      {game.live && settings.features.rewardTotal && collected?.enabled && (
        <div className="board-total">
          <span aria-hidden="true">{REWARD_ICONS[collected.icon as RewardIcon] ?? REWARD_ICONS.star}</span>
          <strong>{collected.total + earnedNow}</strong>
          <span>{collected.name}</span>
        </div>
      )}
      {children}
      {(settings.features.assentBreak || talk) && (
        <div className="toolbar break-bar">
          {settings.features.assentBreak && (
            <button className="btn secondary" onClick={() => void game.takeBreak(breakTrial)}>I need a break</button>
          )}
          {talk && (
            <button className="btn secondary" aria-haspopup="dialog" onClick={openTalkBoard}>
              <span aria-hidden="true">💬</span> Talk
            </button>
          )}
        </div>
      )}
    </>
  );
}

export function PromptLine({ game, text, onRepeat }: { game: Game; text: string; onRepeat?: () => void }) {
  const talkOpen = useTalkBoardOpen();
  const wasOpen = useRef(talkOpen);
  useEffect(() => {
    if (wasOpen.current && !talkOpen && !game.textPrompt) (onRepeat ?? (() => game.say(text)))();
    wasOpen.current = talkOpen;
  }, [talkOpen]);
  return (
    <div className="g-prompt" aria-live="polite">
      <span className="g-prompt-text">{text}</span>
      {!game.textPrompt && (
        <button type="button" className="g-say" aria-label="Say it again" onClick={onRepeat ?? (() => game.say(text))}>
          <span aria-hidden="true">🔊</span>
        </button>
      )}
    </div>
  );
}

export function useRoundTimer(done: boolean, transitionMs: number, next: () => void, hold = 700) {
  const nextRef = useRef(next);
  nextRef.current = next;
  const [fading, setFading] = useState(false);
  useEffect(() => {
    if (!done) { setFading(false); return; }
    const a = window.setTimeout(() => setFading(true), hold);
    const b = window.setTimeout(() => { setFading(false); nextRef.current(); }, hold + transitionMs);
    return () => { window.clearTimeout(a); window.clearTimeout(b); };
  }, [done, transitionMs, hold]);
  return fading;
}
